import {PageSettings} from '../../model/page-settings.model';
import {SalesPageSettings} from '../../model/sales-page-settings.model';
import * as SalesOfferActions from './sales-offer.actions';
import {
  updateStateSalesOfferWithBidDealerStatus,
  updateStateSalesOfferWithBidGeneralStatus
} from '../common-state-functions';
import {AUCTION_TYPES} from '../../misc/constants';

// This is the redux sub-store 'sales-offer' defined by the interface State and its reducer function

/**
 * The interface defining the redux sub-store
 */
export interface StateSalesOffer {
  offers: OfferDto[];
  pageSettings: SalesPageSettings;
}

// default values - will be overridden at demand
const defaultPageSettings = new PageSettings();
defaultPageSettings.size = 25;
defaultPageSettings.page = 0;
defaultPageSettings.sort = 'EXPIRATION';
defaultPageSettings.order = 'ASC';

/**
 * The reducer acts on actions as defined below, and is the only way to update state
 * The JS spread operator for objects ('...') is used to update a given property of our state
 * while leaving the remaining properties of the state unchanged
 *
 * @param {StateSalesOffer} state: the initial state of the redux sub-store
 * @param {OfferAction} action: the actions that this sub-store is managing
 * @returns {any} updated state of the redux sub-store
 */
export function salesOfferReducer(state: StateSalesOffer = {
  offers: undefined,
  pageSettings: defaultPageSettings
},
action: SalesOfferActions.SalesOfferAction) {
  switch (action.type) {
  case SalesOfferActions.UPDATED_OFFERS:
    return {
      ...state,
      offers: action.payload // update our stored offerDtos with the action's payload
    };
  case SalesOfferActions.UPDATE_OFFER:
    return updateOffer(state, action.payload);
  case SalesOfferActions.UPDATE_PAGE_SETTINGS:
    return {
      ...state,
      pageSettings: action.payload
    };
  case SalesOfferActions.UPDATE_PAGE_SETTINGS_PAGE:
    return {
      ...state,
      pageSettings: {
        ...state.pageSettings,
        page: action.page
      }
    };
  case SalesOfferActions.UPDATE_BID_STATUS:
    return updateStateWithBidStatus(state, action.payload);
  case SalesOfferActions.UPDATE_BID_STATUS_GENERAL:
    return updateStateSalesOfferWithBidGeneralStatus(state, action.payload);
  case SalesOfferActions.UPDATE_BID_STATUS_DEALER_SPECIFIC:
    return updateStateSalesOfferWithBidDealerStatus(state, action.payload);
  default:
    return state;
  }
}

function updateOffer(state: StateSalesOffer, payload: OfferDto): StateSalesOffer {
  const index = state.offers['content'].findIndex(findOffer => payload.id === findOffer.id);

  const newOffers = structuredClone(state.offers);
  newOffers['content'].splice(index, 1, payload);

  return {
    ...state,
    offers: {
      ...state.offers,
      ...newOffers
    }
  };
}

/**
 * @deprecated remove when UCS_539_SPLIT_WS_BID_STATUS_MESSAGE is removed
 *
 * Update offer state with new bid status information.
 *
 * Only the current distribution channel is updated, as other channels will be newly fetched from backend on change.
 *
 * @param state the offer state to update
 * @param bidStatus the bid status to update the offer state with
 *
 * @return the update offer state
 */
function updateStateWithBidStatus(state: StateSalesOffer, bidStatus: BidStatusDto): StateSalesOffer {
  updateOfferInOffers(state.offers, bidStatus);
  return state;
}

/**
 * @deprecated remove when UCS_539_SPLIT_WS_BID_STATUS_MESSAGE is removed
 *
 * Updates an offer of our (sub-)state with the updated information from bid status dto.
 *
 * @param offers    the offer dto array containing the auction offer to update
 * @param bidStatus the new bid status to update the auction offer with
 */
function updateOfferInOffers(offers: OfferDto[], bidStatus: BidStatusDto) {
  if (offers) {
    // find the offer we want to update
    const offerToUpdate = offers['content']
      .find(offer => offer.id === bidStatus.offerId && AUCTION_TYPES.includes(offer.offerType.data));

    if (offerToUpdate) {
      setBidStatusOfferToUpdate(bidStatus, offerToUpdate);
    }
  }
}

/**
 * @deprecated remove when UCS_539_SPLIT_WS_BID_STATUS_MESSAGE is removed
 */
function setBidStatusOfferToUpdate(bidStatus: BidStatusDto, offerToUpdate) {
  if (bidStatus.bidStatus) {
    offerToUpdate.bidStatus = bidStatus.bidStatus;
  }
  if (bidStatus.expiration) {
    offerToUpdate.expiration = bidStatus.expiration;
  }
  if (bidStatus.highestBid) {
    offerToUpdate.highestBid = bidStatus.highestBid;
  }
  if (bidStatus.maximumBid) {
    offerToUpdate.maximumBid = bidStatus.maximumBid;
  }
  if (bidStatus.myLastHighestBid) {
    offerToUpdate.myLastHighestBid = bidStatus.myLastHighestBid;
  }
  if (bidStatus.myLastMaximumBid) {
    offerToUpdate.myLastMaximumBid = bidStatus.myLastMaximumBid;
  }
  if (bidStatus.minimumBid) {
    offerToUpdate.minimumBid = bidStatus.minimumBid;
  }
}
